import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ExternalLink, Globe, Share2 } from "lucide-react";
import { useGet } from "../lib/hooks";
import { PageHeader } from "../components/Common";

export function PresenceSite() {
  const { sid } = useParams();
  const [p] = useGet("/presence");
  const s = p?.websites?.find((x) => x.id === sid);
  if (!p) return null;
  if (!s) return <p className="text-sm text-muted-foreground" data-testid="presence-site-missing">This website isn't in Presence any more.</p>;
  return (
    <div className="-m-5 flex h-[calc(100dvh-3.5rem)] flex-col sm:-m-8 lg:h-[100dvh]" data-testid="presence-site">
      <div className="flex items-center gap-3 border-b border-[#C2A462]/15 bg-[#0B1424] px-4 py-2">
        <Globe className="h-4 w-4 shrink-0 text-[#C2A462]" />
        <div className="min-w-0 flex-1 truncate text-sm"><span className="font-medium">{s.name || s.url}</span><span className="ml-2 text-xs text-muted-foreground">{s.url}</span></div>
        <span className="hidden text-[11px] text-muted-foreground xl:inline">Blank page? That site doesn't allow being shown inside other apps.</span>
        <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#C2A462]/50 px-3 py-1 text-xs text-[#EBD6A0] hover:bg-[#C2A462]/10" data-testid="presence-open-new-tab"><ExternalLink className="h-3.5 w-3.5" />Open in new tab</a>
      </div>
      <iframe src={s.url} title={s.name || "Website"} className="min-h-0 w-full flex-1 border-0 bg-white" data-testid="presence-iframe" />
    </div>
  );
}

export default function PresenceOverview() {
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/presence").then((r) => setP(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <p className="text-sm text-muted-foreground" data-testid="presence-forbidden">Presence hasn't been shared with you yet.</p>;
  if (!p) return null;
  return (
    <div data-testid="presence-page">
      <PageHeader overline="Presence" title="Our online presence" desc="Our websites, social media and information for the team." />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-md border bg-card p-5"><div className="mb-3 font-semibold">Websites</div>
          {p.websites.map((s) => s.ours
            ? <Link key={s.id} to={`/app/presence/site/${s.id}`} className="flex items-center gap-2 border-b py-2 text-sm hover:text-[#EBD6A0]" data-testid={`presence-site-link-${s.id}`}><Globe className="h-4 w-4 text-[#C2A462]" />{s.name || s.url}<span className="text-[11px] text-muted-foreground">(ours)</span></Link>
            : <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border-b py-2 text-sm hover:text-[#EBD6A0]" data-testid={`presence-site-link-${s.id}`}><Globe className="h-4 w-4 text-[#C2A462]" />{s.name || s.url}<ExternalLink className="ml-auto h-3.5 w-3.5 opacity-60" /></a>)}
          {!p.websites.length && <p className="text-sm text-muted-foreground">No websites yet.</p>}</div>
        <div className="rounded-md border bg-card p-5"><div className="mb-3 font-semibold">Social media</div>
          {p.socials.map((s) => <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border-b py-2 text-sm hover:text-[#EBD6A0]" data-testid={`presence-social-link-${s.id}`}><Share2 className="h-4 w-4 text-[#C2A462]" />{s.label || s.platform}<ExternalLink className="ml-auto h-3.5 w-3.5 opacity-60" /></a>)}
          {!p.socials.length && <p className="text-sm text-muted-foreground">No social media linked yet.</p>}</div>
        {p.info && <div className="rounded-md border bg-card p-5 lg:col-span-2"><div className="mb-2 font-semibold">Information</div><p className="whitespace-pre-line text-sm" data-testid="presence-info-view">{p.info}</p></div>}
      </div>
    </div>
  );
}
